import type { CellSelectionState, RowData } from "@tanstack/react-table";
import type { DataGridCellAddress } from "../cellGridTypes";
import type { DataGridColumn, DataGridTable } from "../columns/types";
import type { DataGridInstance } from "../types";

export const isSameCellAddress = (
    a: DataGridCellAddress,
    b: DataGridCellAddress,
) => a.rowId === b.rowId && a.columnId === b.columnId;

/** The first row that takes focus: separator rows take none. */
export const findFirstFocusableRow = <TRow extends { original: TData }, TData>(
    rows: readonly TRow[],
    isSeparator: (row: TData) => boolean,
) => rows.find((row) => !isSeparator(row.original));

/** The focused cell: the anchor of the last range, as TanStack defines it. */
export const getFocusedAddress = (
    ranges: CellSelectionState,
): DataGridCellAddress | null => {
    const active = ranges[ranges.length - 1];
    return active
        ? { rowId: active.anchorRowId, columnId: active.anchorColumnId }
        : null;
};

/** The focused cell's row id, or null. */
export const selectFocusedRowId = (ranges: CellSelectionState) =>
    getFocusedAddress(ranges)?.rowId ?? null;

/** The grid cell holding `target`, by its row and column ids. */
export const getCellAddressFromTarget = (
    target: EventTarget | null | undefined,
): DataGridCellAddress | null => {
    const cellElement =
        target instanceof Element
            ? target.closest<HTMLElement>("[role='gridcell'][data-column-id]")
            : null;
    const rowId =
        cellElement?.closest<HTMLElement>("[data-row-id]")?.dataset.rowId;
    const columnId = cellElement?.dataset.columnId;
    return rowId && columnId ? { rowId, columnId } : null;
};

/** Visible leaf columns in display order: start pins, center, end pins. */
export const getDisplayColumns = <TData extends RowData>(
    table: DataGridTable<TData>,
) => [
    ...table.getStartVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getEndVisibleLeafColumns(),
];

/** Whether cell navigation can land on the column's cells. */
export const isNavigableColumn = <TData extends RowData>(
    columnDef: DataGridColumn<TData>,
) => columnDef.enableCellSelection !== false;

/** Ids of the display columns whose definition passes `predicate`, in display order. */
const getColumnIdsWhere = <TData extends RowData>(
    table: DataGridTable<TData>,
    predicate: (columnDef: DataGridColumn<TData>) => boolean,
) =>
    getDisplayColumns(table)
        .filter((column) => predicate(column.columnDef))
        .map((column) => column.id);

/** Column ids cell navigation can land on, in display order. */
export const getNavigableColumnIds = <TData extends RowData>(
    table: DataGridTable<TData>,
) => getColumnIdsWhere(table, isNavigableColumn);

/**
 * Whether list editing gives the column's cells focus: the cell-navigation
 * columns minus the drag handle, which grabs rows rather than holds a value.
 */
export const isListNavigableColumn = <TData extends RowData>(
    columnDef: DataGridColumn<TData>,
) => isNavigableColumn(columnDef) && columnDef.meta?.kind !== "dragHandle";

/** Column ids list editing steps through within a row, in display order. */
export const getListNavigableColumnIds = <TData extends RowData>(
    table: DataGridTable<TData>,
) => getColumnIdsWhere(table, isListNavigableColumn);

/**
 * Focus returns to the focused cell, not just its row: cell navigation, or a
 * list-editing grid whose focus is on a cell.
 */
export const isCellFocusTarget = <TData extends RowData>(
    instance: DataGridInstance<TData>,
) => instance.hasCellNavigation || instance.atoms.listCellFocus.get();

/** Column id stored with the focused row in row mode. */
export const getRowModeColumnId = <TData extends RowData>(
    table: DataGridTable<TData>,
): string | null =>
    getNavigableColumnIds(table)[0] ?? getDisplayColumns(table)[0]?.id ?? null;

const HEADER_FOCUS_SELECTOR = "[data-dt-header-focus]";

const getHeaderTargets = (root: HTMLElement | null) =>
    root
        ? Array.from(root.querySelectorAll<HTMLElement>(HEADER_FOCUS_SELECTOR))
        : [];

/** Focuses a column's header, or the first header; false when none exist. */
export const focusHeader = (
    root: HTMLElement | null,
    columnId: string | null,
): boolean => {
    const targets = getHeaderTargets(root);
    const target =
        targets.find((element) => element.dataset.columnId === columnId) ??
        targets[0];
    target?.focus();
    return target !== undefined;
};

/** Moves header focus by `offset`, or to `"first"` / `"last"`. */
export const moveHeaderFocus = ({
    root,
    from,
    to,
}: {
    root: HTMLElement | null;
    from: HTMLElement;
    to: number | "first" | "last";
}): boolean => {
    const targets = getHeaderTargets(root);
    if (targets.length === 0) {
        return false;
    }
    const currentIndex = targets.findIndex((element) => element.contains(from));
    let nextIndex = 0;
    if (to === "last") {
        nextIndex = targets.length - 1;
    } else if (to !== "first") {
        nextIndex = Math.min(
            Math.max(currentIndex + to, 0),
            targets.length - 1,
        );
    }
    targets[nextIndex]?.focus();
    return true;
};

/**
 * Roving tabindex: the focused element is the grid's one tab stop, other
 * focus targets are reachable by keys only, and non-targets get none.
 */
export const getRovingTabIndex = ({
    isFocusTarget,
    isFocused,
}: {
    isFocusTarget: boolean;
    isFocused: boolean;
}) => {
    if (!isFocusTarget) {
        return undefined;
    }
    return isFocused ? 0 : -1;
};

/**
 * The nearest focusable row to `rowIndex` (clamped), searching away from
 * `fromIndex` first: separator rows take no focus.
 */
export const findFocusableRowIndex = <TData extends RowData>({
    rows,
    isSeparator,
    rowIndex,
    fromIndex,
}: {
    rows: ReadonlyArray<{ original: TData }>;
    isSeparator: (row: TData) => boolean;
    rowIndex: number;
    fromIndex: number;
}) => {
    const start = Math.min(Math.max(rowIndex, 0), rows.length - 1);
    const step = start < fromIndex ? -1 : 1;
    for (const direction of [step, -step]) {
        for (
            let index = start;
            index >= 0 && index < rows.length;
            index += direction
        ) {
            const row = rows[index];
            if (row && !isSeparator(row.original)) {
                return index;
            }
        }
    }
    return -1;
};

/** The first focusable row's index; moving up from it leaves the rows. */
export const findFirstFocusableRowIndex = <TData extends RowData>(
    rows: ReadonlyArray<{ original: TData }>,
    isSeparator: (row: TData) => boolean,
) => findFocusableRowIndex({ rows, isSeparator, rowIndex: 0, fromIndex: 0 });
